import {FC, Fragment} from 'react';
import {toNumber} from 'lodash';
import {useParams} from 'react-router-dom';

import {Button, FormControl, FormHelperText, MenuItem, Select, TextField} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import InputLabel from '@mui/material/InputLabel';
import {Controller, useForm} from 'react-hook-form';
import {documentAddModalClose, useContractDocumentAddModalStore} from '../../stores/contract-document-add-modal.store';
import {ContractFormFileField} from '../contract-form-file-field';
import {ContractModal} from '../contract-modal';
import {useGetContractsCidDocumentsRefresh, usePostContractsCidDocuments} from '../../../../global/services/contracts';
import {serialize} from '../../../../shared/utils/serialize-query-params';
import {useRefreshCompanyTariff} from '../../../company-tariff';

interface FromValues {
  docType: 'SERVICE_REQUESTS' | 'OTHER' | '';
  description: string;
  file: File | null;
}

const DEFAULT_VALUES = {
  docType: '',
  description: '',
  file: null,
} as const;

export const AddDocumentModal: FC = () => {
  const {id} = useParams();
  const open = useContractDocumentAddModalStore((s) => s.open);
  const {mutate, loading} = usePostContractsCidDocuments();
  const refreshCidDocuments = useGetContractsCidDocumentsRefresh();
  const refreshCompanyTariff = useRefreshCompanyTariff();
  const formApi = useForm<FromValues>({mode: 'onChange', defaultValues: DEFAULT_VALUES});
  const {isDirty} = formApi.formState;

  const handleClose = () => {
    formApi.reset(DEFAULT_VALUES);
    documentAddModalClose();
  };

  const afterMutateCallback = () => {
    refreshCidDocuments();
    refreshCompanyTariff();
    handleClose();
  };

  const handleFormSubmit = formApi.handleSubmit(async (data) => {
    if (!data.file || !data.docType) return;

    await mutate(
      [
        toNumber(id),
        {description: data.description, docTypes: [data.docType]},
        {docFiles: [data.file]},
        {paramsSerializer: {serialize}},
      ],
      afterMutateCallback,
    );
  });

  return (
    <ContractModal
      open={open}
      title="Добавить документ"
      footer={
        <Fragment>
          <Button variant="contained" onClick={handleClose}>
            Отменить
          </Button>
          <LoadingButton variant="outlined" onClick={handleFormSubmit} disabled={!isDirty} loading={loading}>
            Добавить
          </LoadingButton>
        </Fragment>
      }
    >
      <Controller
        control={formApi.control}
        name="file"
        rules={{required: 'Выберите файл'}}
        render={({field: {onChange, value}, fieldState: {error}}) => (
          <ContractFormFileField file={value} error={error} onChange={onChange} />
        )}
      />
      <Controller
        control={formApi.control}
        name="docType"
        rules={{required: 'Обязательно для заполнения'}}
        render={({field, fieldState: {error}}) => (
          <FormControl fullWidth variant="standard" error={!!error}>
            <InputLabel>Тип документа</InputLabel>
            <Select label="Тип документа" {...field}>
              <MenuItem value="SERVICE_REQUESTS">Заявки</MenuItem>
              <MenuItem value="OTHER">Другое</MenuItem>
            </Select>
            {!!error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
      <Controller
        control={formApi.control}
        name="description"
        rules={{required: 'Обязательно для заполнения'}}
        render={({field, fieldState: {error}}) => (
          <TextField
            label="Описание"
            fullWidth
            variant="standard"
            {...field}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </ContractModal>
  );
};
